/// <reference types="vite/client" />
declare const __API_DOMAIN__: string;

import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    officeType: '',
    pricing: '',
    expectation: '',
    expectationOther: '',
    opinion: '',
    email: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPrivacyAgreed, setIsPrivacyAgreed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 폼 데이터 검증
    if (!formData.officeType || !formData.pricing || !formData.expectation || !formData.email) {
      alert('모든 필수 항목을 입력해주세요.');
      return;
    }

    if (formData.expectation === '기타' && !formData.expectationOther.trim()) {
      alert('기대하는 효과를 입력해주세요.');
      return;
    }

    if (!isPrivacyAgreed) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    try {
      const response = await fetch(`${__API_DOMAIN__}/api/v1/landing/apply-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('신청 중 오류가 발생했습니다.');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('신청 오류:', error);
      alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 네비게이션 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/05ed6a65-e513-4100-aee1-1a85ef27ce04.png" 
              alt="로고" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-xl font-bold transition-colors ${
              isScrolled ? 'text-[#1F2A44]' : 'text-white'
            }`}>
              taxchecker01
            </span>
          </div>
          <button 
            onClick={() => {
              document.getElementById('beta-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-6 py-2.5 bg-[#1F2A44] text-white text-sm font-medium rounded-md hover:bg-[#2a3b5a] transition-colors whitespace-nowrap cursor-pointer"
          >
            베타 신청
          </button>
        </div>
      </nav>

      {/* 히어로 섹션 */}
      <section className="relative pt-24 pb-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F2A44] via-[#2a3b5a] to-[#1F2A44]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center pt-20">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8" style={{ lineHeight: '1.6' }}>
            세무사님, 당신의 귀한 시간을<br />엑셀검수에 낭비하고 계시진 않나요?
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-4" style={{ lineHeight: '2.0' }}>
            잡일은 저희가 모조리 걷어내겠습니다.
          </p>
          <p className="text-xl md:text-2xl text-gray-200 mb-12" style={{ lineHeight: '2.0' }}>
            다만, 놓치기 쉬운 지점은 미리 알려드립니다.
          </p>
          <button 
            onClick={() => {
              document.getElementById('beta-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-10 py-4 bg-[#F2C94C] text-[#1F2A44] text-lg font-semibold rounded-lg hover:bg-[#e6bd42] transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            서비스 베타 신청
          </button>
        </div>
      </section>

      {/* 문제 인식 섹션 */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-10">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                <i className="ri-file-excel-2-line text-4xl text-[#1F2A44]"></i>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                  "엑셀을 열기 전에는 어디가 문제인지 알 수 없어요"
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  업로드 전까지는 어떤 항목이 주의가 필요한지 파악할 수 없습니다.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                <i className="ri-error-warning-line text-4xl text-[#F2C94C]"></i>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                  "다 보고 나서야 '아, 이건 먼저 걸러졌어야 했네'라고 느껴요"
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  검토를 완료한 후에야 초기 단계에서 확인했어야 할 사항을 발견합니다.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                <i className="ri-time-line text-4xl text-[#1F2A44]"></i>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                  "판단보다 자료 상태 확인에 에너지가 먼저 소모되어요"
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  본질적인 판단 업무 이전에 자료 정합성 확인으로 시간이 흐릅니다.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-white rounded-lg border-l-4 border-[#F2C94C]">
            <p className="text-2xl text-[#1F2A44] font-medium leading-relaxed text-center">
              문제는 판단이 아니라,<br />
              판단 이전에 확인해야 할 포인트가 매번 달라진다는 점입니다.
            </p>
          </div>
        </div>
      </section>

      {/* 서비스 역할 정의 섹션 */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1F2A44] mb-6">
              이 서비스가 하는 일
            </h2>
            <p className="text-xl text-gray-600">
              명확한 역할 정의로 불필요한 부담을 덜어드립니다
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="p-8 bg-gray-50 rounded-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-[#F2C94C]/20 rounded-lg mb-6">
                <i className="ri-alert-line text-3xl text-[#F2C94C]"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-4">
                주의가 필요한 부분 표시
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                업로드된 엑셀에서 한 번 더 확인이 필요한 항목을 미리 표시합니다.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-[#F2C94C]/20 rounded-lg mb-6">
                <i className="ri-notification-line text-3xl text-[#F2C94C]"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-4">
                오류 가능성 경고 제공
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                업로드 오류로 이어질 수 있는 항목에 대해 사전 경고를 제공합니다.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-[#F2C94C]/20 rounded-lg mb-6">
                <i className="ri-information-line text-3xl text-[#F2C94C]"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-4">
                확인 권유 안내
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                다시 한 번 검토해볼 만한 지점을 권유 형태로 안내합니다.
              </p>
            </div>
          </div>

          <div className="p-10 bg-[#1F2A44] rounded-lg text-center">
            <p className="text-2xl text-white font-semibold mb-2">
              이 서비스는 판단하지 않습니다.
            </p>
            <p className="text-xl text-gray-300">
              판단에 필요한 힌트만 제공합니다.
            </p>
          </div>
        </div>
      </section>

      {/* 주요 확인 항목 섹션 */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1F2A44] mb-6">
              실무에서 자주 놓치는 체크 포인트
            </h2>
            <p className="text-xl text-gray-600">
              업로드 전 확인이 필요한 주요 항목을 미리 표시하며,세무사가 확인 후 일괄 수정 기능을 제공합니다.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-8 rounded-lg border-l-4 border-[#F2C94C]">
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-3">
                날짜 형식 혼재
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                서로 다른 날짜 형식이 한 파일에 섞여 있는 경우를 표시합니다.<br />
                <span className="text-sm text-gray-500">(예: 2026-01-01, 2026.1.1, 텍스트 날짜)</span>
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border-l-4 border-[#F2C94C]">
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-3">
                합계와 세부 금액 불일치
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                합계 금액과 개별 금액의 합이 맞지 않는 항목을 알려줍니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border-l-4 border-[#F2C94C]">
              <h3 className="text-xl font-semibold text-[#1F2A44] mb-3">
                필수 값 누락
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                값이 있어야 하는데 비어 있는 행이나 칸을 표시합니다.
              </p>
            </div>
          </div>

          <div className="mt-12 p-8 bg-white rounded-lg text-center">
            <p className="text-lg text-[#1F2A44] font-medium leading-relaxed">
              이 외에도 실무에서 필요한 다양한 체크 포인트를<br />
              지속적으로 추가해 나갑니다.
            </p>
          </div>
        </div>
      </section>

      {/* 실제 사용 시점 섹션 */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1F2A44] mb-6">
              실제 사용 흐름
            </h2>
            <p className="text-xl text-gray-600">
              간단한 4단계로 업무 효율이 달라집니다
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#F2C94C] transform -translate-x-1/2 hidden md:block"></div>

            <div className="space-y-12">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2 md:text-right">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F2C94C] text-[#1F2A44] rounded-full text-2xl font-bold mb-4">
                    1
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                    엑셀 업로드
                  </h3>
                  <p className="text-lg text-gray-600">
                    더존 또는 위하고 업로드용 엑셀 파일을 올립니다.
                  </p>
                </div>
                <div className="md:w-1/2"></div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2"></div>
                <div className="md:w-1/2">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F2C94C] text-[#1F2A44] rounded-full text-2xl font-bold mb-4">
                    2
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                    경고 / 알림 / 확인 권유
                  </h3>
                  <p className="text-lg text-gray-600">
                    주의가 필요한 항목과 확인이 권장되는 부분을 확인합니다.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2 md:text-right">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F2C94C] text-[#1F2A44] rounded-full text-2xl font-bold mb-4">
                    3
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                    세무사가 직접 판단
                  </h3>
                  <p className="text-lg text-gray-600">
                    제공된 정보를 바탕으로 전문가로서 최종 판단을 내립니다.
                  </p>
                </div>
                <div className="md:w-1/2"></div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/2"></div>
                <div className="md:w-1/2">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F2C94C] text-[#1F2A44] rounded-full text-2xl font-bold mb-4">
                    4
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1F2A44] mb-3">
                    직접 확인 후 일괄 수정 기능
                  </h3>
                  <p className="text-lg text-gray-600">
                    확인된 내용을 바탕으로 필요한 항목을 일괄로 수정할 수 있습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gray-50 rounded-lg border-l-4 border-[#1F2A44]">
            <p className="text-2xl text-[#1F2A44] font-medium leading-relaxed text-center">
              엑셀을 덜 보는 게 아니라,<br />
              봐야 할 부분부터 보게 됩니다.
            </p>
          </div>
        </div>
      </section>

      {/* 가격 제시 섹션 */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1F2A44] mb-6">
              명확한 가격 정책
            </h2>
            <p className="text-xl text-gray-600">
              꼭 필요한 기능만 담아, 부담스럽지 않은 가격입니다
            </p>
          </div>

          <div className="bg-white rounded-2xl p-12 text-center border-2 border-[#F2C94C]">
            <div className="mb-8">
              <div className="text-lg text-gray-600 mb-2">월 구독</div>
              <div className="text-6xl font-bold text-[#1F2A44] mb-2">
                11,000<span className="text-3xl">원</span>
              </div>
              <div className="text-base text-gray-500">부가세 별도</div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center gap-3">
                <i className="ri-check-line text-2xl text-[#F2C94C]"></i>
                <span className="text-lg text-gray-700">대용량 데이터 일괄 정밀 스캔</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <i className="ri-check-line text-2xl text-[#F2C94C]"></i>
                <span className="text-lg text-gray-700">경고 / 알림 / 권유 항목 표시</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <i className="ri-check-line text-2xl text-[#F2C94C]"></i>
                <span className="text-lg text-gray-700">원클릭 데이터 표준화</span>
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-lg">
              <p className="text-lg text-[#1F2A44] font-medium">
                세무사의 시간을 돌려드립니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + 베타 신청 섹션 */}
      <section id="beta-form" className="py-24 px-6 bg-[#1F2A44]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              곧 출시 됩니다.
            </h2>
            <p className="text-xl text-gray-300">
              신청해주신 이메일 주소로 가장 먼저 출시 소식을 전달해드립니다.
            </p>
          </div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="bg-white rounded-lg p-10 space-y-8">
              <div>
                <label className="block text-base font-semibold text-[#1F2A44] mb-3">
                  사무소 형태
                </label>
                <select
                  name="officeType"
                  value={formData.officeType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-[#F2C94C] focus:outline-none cursor-pointer"
                  required
                >
                  <option value="">선택해주세요</option>
                  <option value="1인">1인 세무사무소</option>
                  <option value="직원포함">직원 포함 세무사무소</option>
                </select>
              </div>

              <div>
                <label className="block text-base font-semibold text-[#1F2A44] mb-3">
                  월 11,000원 가격은 어떤가요?
                </label>
                <div className="space-y-3">
                  {['싸다', '적당하다', '비싸다'].map((option) => (
                    <label key={option} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="pricing"
                        value={option}
                        checked={formData.pricing === option}
                        onChange={handleChange}
                        className="w-5 h-5 cursor-pointer"
                        required
                      />
                      <span className="text-base text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-[#1F2A44] mb-3">
                  가장 기대하는 효과
                </label>
                <div className="space-y-3">
                  {[
                    '판단 전 체크 포인트 정리',
                    '반복 오류 사전 인지',
                    '엑셀 검토 집중도 향상',
                    '기타'
                  ].map((option) => (
                    <label key={option} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="expectation"
                        value={option}
                        checked={formData.expectation === option}
                        onChange={handleChange}
                        className="w-5 h-5 cursor-pointer"
                        required
                      />
                      <span className="text-base text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.expectation === '기타' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      name="expectationOther"
                      value={formData.expectationOther}
                      onChange={handleChange}
                      placeholder="기대하는 효과를 입력해주세요"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-[#F2C94C] focus:outline-none"
                      required
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-base font-semibold text-[#1F2A44] mb-3">
                  의견 (선택사항)
                </label>
                <textarea
                  name="opinion"
                  value={formData.opinion}
                  onChange={handleChange}
                  placeholder="서비스에 대한 의견이나 제안사항이 있으시면 자유롭게 작성해주세요"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-[#F2C94C] focus:outline-none resize-none"
                />
                <div className="text-sm text-gray-500 mt-2 text-right">
                  {formData.opinion.length}/500
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-[#1F2A44] mb-3">
                  이메일 주소 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@example.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:border-[#F2C94C] focus:outline-none"
                  required
                />
              </div>

              <div className="border-t-2 border-gray-100 pt-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPrivacyAgreed}
                    onChange={(e) => setIsPrivacyAgreed(e.target.checked)}
                    className="w-5 h-5 mt-0.5 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-base text-gray-700">
                    <span className="font-semibold text-[#1F2A44]">[필수]</span> 개인정보 수집 및 이용에 동의합니다.
                  </span>
                </label>
                
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-600 space-y-2">
                  <div><strong>수집 항목:</strong> 이메일 주소</div>
                  <div><strong>수집 목적:</strong> 베타서비스 신청 확인 및 베타서비스 관련 안내</div>
                  <div><strong>보유 기간:</strong> 베타서비스 종료 후 6개월 또는 이용자의 삭제 요청 시 즉시 파기</div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="mt-3 text-sm text-[#1F2A44] underline hover:text-[#2a3b5a] cursor-pointer"
                >
                  개인정보 처리방침 보기
                </button>
              </div>

              <button
                type="submit"
                disabled={!isPrivacyAgreed}
                className={`w-full py-4 text-lg font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  isPrivacyAgreed
                    ? 'bg-[#F2C94C] text-[#1F2A44] hover:bg-[#e6bd42]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                베타 신청 완료
              </button>
            </form>
          ) : (
            <div className="bg-white rounded-lg p-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#F2C94C] rounded-full mx-auto mb-6">
                <i className="ri-check-line text-5xl text-[#1F2A44]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1F2A44] mb-4">
                신청이 완료되었습니다
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                입력하신 이메일로 베타 서비스 안내를 보내드리겠습니다.
              </p>
              <p className="text-base text-gray-500">
                감사합니다.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* 개인정보 처리방침 모달 */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-[#1F2A44]">개인정보 처리방침</h3>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="p-6 space-y-6 text-gray-700 leading-relaxed">
              <p>
                본 서비스(이하 "회사")는 개인정보보호법 등 관련 법령을 준수하며, 이용자의 개인정보를 보호하기 위해 다음과 같은 개인정보 처리방침을 수립·공개합니다.
              </p>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">1. 수집하는 개인정보 항목</h4>
                <p className="mb-2">회사는 베타서비스 신청을 위해 아래의 개인정보를 수집합니다.</p>
                <p className="mb-1">수집 항목: 이메일 주소</p>
                <p>수집 방법: 랜딩페이지 베타서비스 신청 폼</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">2. 개인정보의 수집 및 이용 목적</h4>
                <p className="mb-2">수집한 개인정보는 다음 목적에 한해 이용됩니다.</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>베타서비스 신청 확인 및 안내</li>
                  <li>베타서비스 관련 공지 및 커뮤니케이션</li>
                  <li>서비스 개선을 위한 최소한의 연락</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">3. 개인정보의 보유 및 이용 기간</h4>
                <p className="mb-2">보유 기간: 베타서비스 종료 후 6개월</p>
                <p className="mb-2">단, 이용자가 개인정보 삭제를 요청할 경우 지체 없이 파기합니다.</p>
                <p>관련 법령에 따라 보관이 필요한 경우에는 해당 법령에서 정한 기간 동안 보관할 수 있습니다.</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">4. 개인정보의 제3자 제공</h4>
                <p>회사는 이용자의 개인정보를 외부에 제공하지 않습니다. 다만, 법령에 근거한 요청이 있는 경우에 한하여 예외로 합니다.</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">5. 개인정보의 처리 위탁</h4>
                <p>회사는 원칙적으로 개인정보 처리 업무를 외부에 위탁하지 않습니다. 향후 위탁이 발생할 경우, 관련 내용을 본 방침을 통해 안내드릴 예정입니다.</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">6. 이용자의 권리와 행사 방법</h4>
                <p className="mb-2">이용자는 언제든지 본인의 개인정보에 대해 다음 권리를 행사할 수 있습니다.</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-2">
                  <li>개인정보 열람 요청</li>
                  <li>개인정보 정정 및 삭제 요청</li>
                  <li>개인정보 처리 정지 요청</li>
                </ul>
                <p>요청은 이메일을 통해 접수 가능하며, 회사는 지체 없이 조치합니다.</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">7. 개인정보의 파기 절차 및 방법</h4>
                <p className="mb-2">개인정보는 보유 기간이 경과하거나 처리 목적이 달성된 경우 지체 없이 파기합니다.</p>
                <p>전자적 파일 형태: 복구 불가능한 방법으로 삭제</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">8. 개인정보 보호를 위한 조치</h4>
                <p className="mb-2">회사는 개인정보 보호를 위해 다음과 같은 조치를 취하고 있습니다.</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>개인정보 접근 권한 최소화</li>
                  <li>내부 관리 기준 수립</li>
                  <li>개인정보 취급에 대한 관리·감독</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">9. 개인정보 보호책임자</h4>
                <p className="mb-2">개인정보 보호 관련 문의는 아래 연락처로 가능합니다.</p>
                <p className="mb-1">담당자: 개인정보 보호책임자</p>
                <p>연락처: devlshem67@gmail.com</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1F2A44] mb-3">10. 개인정보 처리방침 변경</h4>
                <p>본 개인정보 처리방침은 서비스 정책 또는 법령 변경에 따라 수정될 수 있으며, 변경 시 랜딩페이지를 통해 안내드립니다.</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-full py-3 bg-[#1F2A44] text-white font-semibold rounded-lg hover:bg-[#2a3b5a] transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 푸터 */}
      <footer className="py-12 px-6 bg-[#2a3b5a] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/05ed6a65-e513-4100-aee1-1a85ef27ce04.png" 
                alt="로고" 
                className="h-8 w-8 object-contain"
              />
              <span className="text-lg font-semibold">taxchecker01</span>
            </div>
            <div className="text-sm text-gray-400">
              © 2025 taxchecker01. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
